document.documentElement.classList.remove('no-js');

/* ---------------------------------------------------------
   Core interface controls
   --------------------------------------------------------- */

(() => {
  'use strict';

  const filterToggles = new WeakSet();
  const menuToggles = new WeakSet();
  const finders = new WeakSet();

  const selectWithin = (root, selector) => {
    const result = Array.from(root.querySelectorAll(selector));

    if (root instanceof Element && root.matches(selector)) {
      result.unshift(root);
    }

    return result;
  };

  const initialize = (root = document) => {

    /* =====================================================
       FILTER PANEL
       ===================================================== */

    selectWithin(root, '[data-filter-toggle]').forEach(button => {

      if (filterToggles.has(button)) return;

      filterToggles.add(button);

      const scope =
        button.closest('.shopify-section') ||
        document;

      const panel =
        scope.querySelector('[data-filter-panel]') ||
        document.querySelector('[data-filter-panel]');

      const overlay =
        scope.querySelector('[data-filter-overlay]') ||
        document.querySelector('[data-filter-overlay]');

      if (!panel) return;

      const setOpen = open => {

        panel.classList.toggle(
          'is-open',
          open
        );

        overlay?.classList.toggle(
          'is-open',
          open
        );

        button.setAttribute(
          'aria-expanded',
          open ? 'true' : 'false'
        );

        document.body.style.overflow =
          open ? 'hidden' : '';
      };

      button.setAttribute(
        'aria-expanded',
        panel.classList.contains('is-open')
          ? 'true'
          : 'false'
      );

      button.addEventListener(
        'click',
        () => {

          setOpen(
            !panel.classList.contains('is-open')
          );

        }
      );

      overlay?.addEventListener(
        'click',
        () => setOpen(false)
      );

    });



    /* =====================================================
       MOBILE MENU
       ===================================================== */

    selectWithin(root, '[data-menu-toggle]').forEach(button => {

      if (menuToggles.has(button)) return;

      menuToggles.add(button);

      const scope =
        button.closest('.shopify-section') ||
        document;

      const menu =
        scope.querySelector('[data-menu]') ||
        document.querySelector('[data-menu]');

      if (!menu) return;

      const setOpen = open => {

        menu.classList.toggle(
          'is-open',
          open
        );

        button.setAttribute(
          'aria-expanded',
          open ? 'true' : 'false'
        );

      };

      button.setAttribute(
        'aria-expanded',
        menu.classList.contains('is-open')
          ? 'true'
          : 'false'
      );

      button.addEventListener(
        'click',
        () => {

          setOpen(
            !menu.classList.contains('is-open')
          );

        }
      );

      menu.addEventListener(
        'click',
        event => {

          const target =
            event.target instanceof Element
              ? event.target
              : null;

          if (target?.closest('a')) {
            setOpen(false);
          }

        }
      );

    });



    /* =====================================================
       PRODUCT FINDER
       ===================================================== */

    selectWithin(root, '[data-finder]').forEach(form => {

      if (finders.has(form)) return;

      finders.add(form);

      form.addEventListener(
        'submit',
        event => {

          event.preventDefault();

          const params =
            new URLSearchParams();

          form
            .querySelectorAll('select')
            .forEach(select => {

              if (select.value) {

                params.append(
                  'filter.p.tag',
                  select.value
                );

              }

            });

          const query =
            params.toString();

          const destination =
            form.dataset.url;

          if (!destination) return;

          window.location.href =
            query
              ? `${destination}?${query}`
              : destination;

        }
      );

    });

  };



  /* =======================================================
     ESCAPE KEY
     ======================================================= */

  document.addEventListener(
    'keydown',
    event => {

      if (event.key !== 'Escape') return;

      const panel =
        document.querySelector(
          '[data-filter-panel].is-open'
        );

      if (panel) {

        panel.classList.remove(
          'is-open'
        );

        document
          .querySelector(
            '[data-filter-overlay].is-open'
          )
          ?.classList.remove(
            'is-open'
          );

        document
          .querySelector(
            '[data-filter-toggle][aria-expanded="true"]'
          )
          ?.setAttribute(
            'aria-expanded',
            'false'
          );

        document.body.style.overflow =
          '';

      }

      const menu =
        document.querySelector(
          '[data-menu].is-open'
        );

      if (menu) {

        menu.classList.remove(
          'is-open'
        );

        document
          .querySelector(
            '[data-menu-toggle][aria-expanded="true"]'
          )
          ?.setAttribute(
            'aria-expanded',
            'false'
          );

      }

    }
  );



  /* =======================================================
     SHOPIFY EDITOR SUPPORT
     ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    event => {

      initialize(
        event.target
      );

    }
  );


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(document),
      {
        once: true
      }
    );

  } else {

    initialize(document);

  }

})();





/* ---------------------------------------------------------
   Pointer-following product-card glass
   --------------------------------------------------------- */

(() => {
  'use strict';

  if (window.__vnProductGlass) return;

  window.__vnProductGlass = true;


  const reduceMotion =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );


  const finePointer =
    window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    );


  const states =
    new Map();


  let layoutVersion =
    0;



  const eligible = () =>
    !reduceMotion.matches &&
    finePointer.matches;



  const selectCards = root => {

    const cards =
      Array.from(
        root.querySelectorAll(
          '.product-card'
        )
      );

    if (
      root instanceof Element &&
      root.matches('.product-card')
    ) {

      cards.unshift(root);

    }

    return cards;

  };



  const clearProperties = card => {

    [
      '--glass-x',
      '--glass-y',
      '--glass-rx',
      '--glass-ry'
    ].forEach(key => {

      card.style.removeProperty(
        key
      );

    });

  };



  const resetState = state => {

    if (state.frame) {

      cancelAnimationFrame(
        state.frame
      );

    }

    state.frame =
      0;

    state.rect =
      null;

    state.card.classList.remove(
      'is-glass-active'
    );

    clearProperties(
      state.card
    );

  };



  const detach = card => {

    const state =
      states.get(card);

    if (!state) return;

    resetState(
      state
    );

    state.controller.abort();

    states.delete(
      card
    );

  };



  const attach = card => {

    if (
      !eligible() ||
      states.has(card)
    ) {

      return;

    }


    const controller =
      new AbortController();


    const state = {

      card,

      controller,

      frame: 0,

      rect: null,

      rectVersion: -1,

      x: 0,

      y: 0

    };


    states.set(
      card,
      state
    );



    const refreshRect = () => {

      state.rect =
        card.getBoundingClientRect();

      state.rectVersion =
        layoutVersion;

      return state.rect;

    };



    const reset = () =>
      resetState(state);



    card.addEventListener(
      'pointerenter',
      event => {

        if (
          !eligible() ||
          event.pointerType === 'touch'
        ) {

          return;

        }

        refreshRect();

      },
      {
        passive: true,
        signal: controller.signal
      }
    );



    card.addEventListener(
      'pointermove',
      event => {

        if (
          !eligible() ||
          event.pointerType === 'touch'
        ) {

          reset();

          return;

        }


        state.x =
          event.clientX;

        state.y =
          event.clientY;


        if (state.frame) return;


        state.frame =
          requestAnimationFrame(
            () => {

              state.frame =
                0;


              const box =

                !state.rect ||
                state.rectVersion !== layoutVersion

                  ? refreshRect()

                  : state.rect;


              if (
                !box.width ||
                !box.height
              ) {

                return;

              }


              const px =
                Math.max(
                  0,
                  Math.min(
                    1,
                    (
                      state.x -
                      box.left
                    ) /
                    box.width
                  )
                );


              const py =
                Math.max(
                  0,
                  Math.min(
                    1,
                    (
                      state.y -
                      box.top
                    ) /
                    box.height
                  )
                );


              card.style.setProperty(
                '--glass-x',
                `${(px * 100).toFixed(1)}%`
              );


              card.style.setProperty(
                '--glass-y',
                `${(py * 100).toFixed(1)}%`
              );


              card.style.setProperty(
                '--glass-rx',
                `${(
                  (0.5 - py) *
                  4
                ).toFixed(2)}deg`
              );


              card.style.setProperty(
                '--glass-ry',
                `${(
                  (px - 0.5) *
                  4
                ).toFixed(2)}deg`
              );


              card.classList.add(
                'is-glass-active'
              );

            }
          );

      },
      {
        passive: true,
        signal: controller.signal
      }
    );



    card.addEventListener(
      'pointerleave',
      reset,
      {
        signal:
          controller.signal
      }
    );


    card.addEventListener(
      'pointercancel',
      reset,
      {
        signal:
          controller.signal
      }
    );

  };



  const init = (
    root = document
  ) => {

    if (!eligible()) return;

    selectCards(root)
      .forEach(
        attach
      );

  };



  const resetAll = () => {

    for (
      const state
      of states.values()
    ) {

      resetState(
        state
      );

    }

  };



  const detachAll = () => {

    for (
      const card
      of Array.from(
        states.keys()
      )
    ) {

      detach(card);

    }

  };



  const handleCapabilityChange = () => {

    resetAll();

    if (eligible()) {

      init(document);

    } else {

      detachAll();

    }

  };



  const invalidateLayout = () => {

    layoutVersion +=
      1;

  };


  window.addEventListener(
    'resize',
    invalidateLayout,
    {
      passive: true
    }
  );


  window.addEventListener(
    'scroll',
    invalidateLayout,
    {
      passive: true
    }
  );


  window.addEventListener(
    'blur',
    resetAll
  );


  document.addEventListener(
    'visibilitychange',
    () => {

      if (document.hidden) {
        resetAll();
      }

    }
  );


  reduceMotion.addEventListener(
    'change',
    handleCapabilityChange
  );


  finePointer.addEventListener(
    'change',
    handleCapabilityChange
  );


  document.addEventListener(
    'shopify:section:load',
    event => {

      init(
        event.target
      );

    }
  );


  document.addEventListener(
    'shopify:section:unload',
    event => {

      selectCards(
        event.target
      ).forEach(
        detach
      );

    }
  );


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      {
        once: true
      }
    );

  } else {

    init(document);

  }

})();





/* ---------------------------------------------------------
   Vitality Nest progressive motion
   --------------------------------------------------------- */

(() => {
  'use strict';


  if (window.__vnMotion) return;

  window.__vnMotion =
    true;



  const reduced =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );


  const precise =
    window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    );


  const pending =
    new Set();


  const seen =
    new WeakSet();


  const revealTimers =
    new Map();


  const controls =
    new Map();


  const forms =
    new Map();


  let observer =
    null;


  let layoutVersion =
    0;



  /* =======================================================
     ELEMENTS THAT RECEIVE SCROLL REVEALS
     ======================================================= */

  const revealSelector = [

    '.product-card',

    '.collection-card',

    '.section-heading',

    '.vitality-discovery-card',

    '.vitality-feature',

    '.vitality-finder-step',

    '.vitality-newsletter-inner',

    '.vitality-cart-row',

    '.vitality-search-empty',

    '.vitality-product-details details'

  ].join(', ');



  const selectWithin = (
    root,
    selector
  ) => {

    const result =
      Array.from(
        root.querySelectorAll(
          selector
        )
      );


    if (
      root instanceof Element &&
      root.matches(selector)
    ) {

      result.unshift(
        root
      );

    }


    return result;

  };



  /* =======================================================
     REVEALS
     ======================================================= */

  const finishReveal = element => {

    observer?.unobserve(
      element
    );


    clearTimeout(
      revealTimers.get(
        element
      )
    );


    revealTimers.delete(
      element
    );


    element.classList.remove(
      'vn-motion-reveal-pending',
      'vn-motion-reveal-visible'
    );


    element.style.removeProperty(
      '--vn-reveal-delay'
    );


    pending.delete(
      element
    );

  };



  if (
    'IntersectionObserver' in window
  ) {

    observer =
      new IntersectionObserver(
        entries => {

          const entering =
            entries

              .filter(
                entry =>
                  entry.isIntersecting
              )

              .sort(
                (a, b) =>

                  a.boundingClientRect.top -
                    b.boundingClientRect.top ||

                  a.boundingClientRect.left -
                    b.boundingClientRect.left
              );


          entering.forEach(
            (
              entry,
              index
            ) => {

              const element =
                entry.target;


              observer.unobserve(
                element
              );


              if (
                reduced.matches
              ) {

                finishReveal(
                  element
                );

                return;

              }


              const delay =
                Math.min(
                  index * 45,
                  180
                );


              element.style.setProperty(
                '--vn-reveal-delay',
                `${delay}ms`
              );


              element.classList.add(
                'vn-motion-reveal-visible'
              );


              revealTimers.set(

                element,

                setTimeout(
                  () =>
                    finishReveal(
                      element
                    ),

                  delay + 820
                )

              );

            }
          );

        },

        {

          threshold:
            0,

          rootMargin:
            '0px 0px 40px 0px'

        }
      );

  }



  const registerReveals = root => {

    if (
      !observer ||
      reduced.matches
    ) {

      return;

    }


    selectWithin(
      root,
      revealSelector
    ).forEach(
      element => {

        if (
          seen.has(
            element
          )
        ) {

          return;

        }


        seen.add(
          element
        );


        const rect =
          element.getBoundingClientRect();


        /*
          Keep content already visible on the first paint visible.
          Only below-the-fold content receives reveal preparation.
        */

        if (

          !rect.width ||

          !rect.height ||

          rect.top <
            window.innerHeight + 40 ||

          element.contains(
            document.activeElement
          )

        ) {

          return;

        }


        pending.add(
          element
        );


        element.classList.add(
          'vn-motion-reveal-pending'
        );


        observer.observe(
          element
        );

      }
    );

  };



  /* =======================================================
     MAGNETIC CONTROLS
     ======================================================= */

  const magnetsAllowed = () =>

    precise.matches &&
    !reduced.matches;



  const resetMagnet = state => {

    if (
      state.frame
    ) {

      cancelAnimationFrame(
        state.frame
      );

    }


    state.frame =
      0;


    state.bounds =
      null;


    state.label.style.removeProperty(
      'transform'
    );

  };



  const findMotionLabel = control => {

    const explicit =
      control.querySelector(
        ':scope > [data-vn-motion-label], :scope > .vitality-add-label'
      );


    if (explicit) {

      return {

        label:
          explicit,

        created:
          false

      };

    }


    const firstSpan =
      control.querySelector(
        ':scope > span:first-child'
      );


    if (
      firstSpan &&
      firstSpan.textContent.trim()
    ) {

      return {

        label:
          firstSpan,

        created:
          false

      };

    }


    if (
      control.children.length ||
      !control.textContent.trim()
    ) {

      return null;

    }


    const label =
      document.createElement(
        'span'
      );


    label.dataset.vnMotionLabel =
      '';


    while (
      control.firstChild
    ) {

      label.appendChild(
        control.firstChild
      );

    }


    control.appendChild(
      label
    );


    return {

      label,

      created:
        true

    };

  };



  const detachControl = control => {

    const state =
      controls.get(
        control
      );


    if (!state) return;


    resetMagnet(
      state
    );


    state.controller.abort();


    if (
      state.created &&
      state.label.parentNode === control
    ) {

      while (
        state.label.firstChild
      ) {

        control.insertBefore(

          state.label.firstChild,

          state.label

        );

      }


      state.label.remove();

    }


    controls.delete(
      control
    );

  };



  const detachAllControls = () => {

    for (
      const control
      of Array.from(
        controls.keys()
      )
    ) {

      detachControl(
        control
      );

    }

  };



  const registerControls = root => {

    if (
      !magnetsAllowed()
    ) {

      return;

    }


    selectWithin(

      root,

      [
        '[data-vn-magnetic]',
        'main a.button',
        'main button.button'
      ].join(', ')

    ).forEach(
      control => {

        if (

          controls.has(
            control
          ) ||

          control.closest(
            '.shopify-payment-button'
          ) ||

          control.matches(
            '[disabled], [aria-disabled="true"]'
          )

        ) {

          return;

        }


        const motionLabel =
          findMotionLabel(
            control
          );


        if (
          !motionLabel
        ) {

          return;

        }


        const {
          label,
          created
        } = motionLabel;


        label.classList.add(
          'vn-motion-label'
        );


        const controller =
          new AbortController();


        const state = {

          label,

          controller,

          created,

          frame:
            0,

          bounds:
            null,

          boundsVersion:
            -1,

          x:
            0,

          y:
            0

        };


        controls.set(
          control,
          state
        );



        const refreshBounds = () => {

          state.bounds =
            control.getBoundingClientRect();


          state.boundsVersion =
            layoutVersion;


          return state.bounds;

        };



        control.addEventListener(

          'pointerenter',

          event => {

            if (
              !magnetsAllowed() ||
              event.pointerType !== 'mouse'
            ) {

              return;

            }


            refreshBounds();

          },

          {

            passive:
              true,

            signal:
              controller.signal

          }

        );



        control.addEventListener(

          'pointermove',

          event => {

            if (

              !magnetsAllowed() ||

              event.pointerType !== 'mouse' ||

              control.matches(
                ':focus-visible, [disabled], [aria-busy="true"]'
              )

            ) {

              resetMagnet(
                state
              );

              return;

            }


            state.x =
              event.clientX;


            state.y =
              event.clientY;


            if (
              state.frame
            ) {

              return;

            }


            state.frame =
              requestAnimationFrame(
                () => {

                  state.frame =
                    0;


                  const bounds =

                    !state.bounds ||
                    state.boundsVersion !== layoutVersion

                      ? refreshBounds()

                      : state.bounds;


                  if (
                    !bounds.width ||
                    !bounds.height
                  ) {

                    return;

                  }


                  const x =
                    Math.max(

                      -3,

                      Math.min(

                        3,

                        (
                          (
                            state.x -
                            bounds.left
                          ) /
                          bounds.width -
                          0.5
                        ) * 6

                      )

                    );


                  const y =
                    Math.max(

                      -3,

                      Math.min(

                        3,

                        (
                          (
                            state.y -
                            bounds.top
                          ) /
                          bounds.height -
                          0.5
                        ) * 6

                      )

                    );


                  state.label.style.transform =
                    `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;

                }
              );

          },

          {

            passive:
              true,

            signal:
              controller.signal

          }

        );



        [
          'pointerleave',
          'pointercancel',
          'focusin'
        ].forEach(
          type => {

            control.addEventListener(

              type,

              () =>
                resetMagnet(
                  state
                ),

              {

                signal:
                  controller.signal

              }

            );

          }
        );

      }
    );

  };



  /* =======================================================
     ACCESSIBLE STATUS TEXT
     ======================================================= */

  const visuallyHide = element => {

    Object.assign(

      element.style,

      {

        position:
          'absolute',

        width:
          '1px',

        height:
          '1px',

        padding:
          '0',

        margin:
          '-1px',

        overflow:
          'hidden',

        clip:
          'rect(0, 0, 0, 0)',

        whiteSpace:
          'nowrap',

        border:
          '0'

      }

    );

  };



  /* =======================================================
     PRODUCT FORM LOADING STATE
     ======================================================= */

  const resetForm = state => {

    clearTimeout(
      state.disableTimer
    );


    clearTimeout(
      state.recoveryTimer
    );


    if (
      !state.loading
    ) {

      return;

    }


    state.loading =
      false;


    state.form.removeAttribute(
      'aria-busy'
    );


    if (
      state.button
    ) {

      state.button.disabled =
        state.wasDisabled;


      if (
        state.previousBusy === null
      ) {

        state.button.removeAttribute(
          'aria-busy'
        );

      } else {

        state.button.setAttribute(
          'aria-busy',
          state.previousBusy
        );

      }


      state.button.classList.remove(
        'vn-motion-loading'
      );

    }


    if (
      state.label &&
      state.previousText != null
    ) {

      state.label.textContent =
        state.previousText;

    }


    state.spinnerAnimation?.cancel();


    state.spinner?.remove();


    state.spinner =
      null;


    state.spinnerAnimation =
      null;


    state.status.textContent =
      '';

  };



  const registerForms = root => {

    selectWithin(
      root,
      '.product-form'
    ).forEach(
      form => {

        if (
          !(form instanceof HTMLFormElement) ||
          forms.has(form)
        ) {

          return;

        }


        const action =
          new URL(
            form.action,
            location.href
          );


        if (

          action.origin !==
            location.origin ||

          !/\/cart\/add\/?$/.test(
            action.pathname
          ) ||

          form.method.toLowerCase() !==
            'post'

        ) {

          return;

        }


        const status =
          document.createElement(
            'span'
          );


        status.className =
          'vn-motion-status';


        status.setAttribute(
          'role',
          'status'
        );


        status.setAttribute(
          'aria-live',
          'polite'
        );


        status.setAttribute(
          'aria-atomic',
          'true'
        );


        visuallyHide(
          status
        );


        form.appendChild(
          status
        );


        forms.set(

          form,

          {

            form,

            status,

            loading:
              false

          }

        );

      }
    );

  };



  document.addEventListener(
    'submit',
    event => {

      const state =
        forms.get(
          event.target
        );


      if (
        !state ||
        event.defaultPrevented
      ) {

        return;

      }


      const button =
        event.submitter;


      if (

        !(
          button instanceof
          HTMLButtonElement
        ) ||

        button.name ===
          'checkout' ||

        !button.matches(
          '.button'
        ) ||

        button.formTarget ===
          '_blank'

      ) {

        return;

      }


      if (
        state.loading
      ) {

        event.preventDefault();

        return;

      }


      state.loading =
        true;


      state.button =
        button;


      state.wasDisabled =
        button.disabled;


      state.previousBusy =
        button.getAttribute(
          'aria-busy'
        );


      state.form.setAttribute(
        'aria-busy',
        'true'
      );


      button.setAttribute(
        'aria-busy',
        'true'
      );


      button.classList.add(
        'vn-motion-loading'
      );


      const controlState =
        controls.get(
          button
        );


      if (
        controlState
      ) {

        resetMagnet(
          controlState
        );

      }


      const motionLabel =
        controlState ||
        findMotionLabel(
          button
        );


      if (
        motionLabel?.label
      ) {

        state.label =
          motionLabel.label;


        state.previousText =
          state.label.textContent;


        state.label.textContent =
          'Adding…';

      } else {

        state.label =
          null;


        state.previousText =
          null;

      }



      /* ===============================================
         LOADING SPINNER
         =============================================== */

      state.spinner =
        document.createElement(
          'span'
        );


      state.spinner.className =
        'vn-motion-spinner';


      state.spinner.setAttribute(
        'aria-hidden',
        'true'
      );


      Object.assign(

        state.spinner.style,

        {

          width:
            '14px',

          height:
            '14px',

          flex:
            '0 0 14px',

          border:
            '2px solid currentColor',

          borderRightColor:
            'transparent',

          borderRadius:
            '50%',

          display:
            'inline-block'

        }

      );


      button.appendChild(
        state.spinner
      );


      if (

        !reduced.matches &&

        typeof state.spinner.animate ===
          'function'

      ) {

        state.spinnerAnimation =
          state.spinner.animate(

            [

              {
                transform:
                  'rotate(0deg)'
              },

              {
                transform:
                  'rotate(360deg)'
              }

            ],

            {

              duration:
                700,

              iterations:
                Infinity

            }

          );

      }


      state.status.textContent =
        'Adding item to your bag…';


      state.disableTimer =
        setTimeout(
          () => {

            if (
              event.defaultPrevented
            ) {

              resetForm(
                state
              );

              return;

            }


            if (
              state.loading &&
              button.isConnected
            ) {

              button.disabled =
                true;

            }

          },
          0
        );


      state.recoveryTimer =
        setTimeout(
          () =>
            resetForm(
              state
            ),
          12000
        );

    }
  );



  /* =======================================================
     INITIALIZATION
     ======================================================= */

  const initialize = root => {

    registerReveals(
      root
    );

    registerControls(
      root
    );

    registerForms(
      root
    );

  };



  /* =======================================================
     SHOPIFY SECTION CLEANUP
     ======================================================= */

  const cleanup = root => {

    for (
      const element
      of Array.from(
        pending
      )
    ) {

      if (
        root.contains(
          element
        )
      ) {

        finishReveal(
          element
        );

      }

    }


    for (
      const [
        form,
        state
      ]
      of forms
    ) {

      if (
        !root.contains(
          form
        )
      ) {

        continue;

      }


      resetForm(
        state
      );


      state.status.remove();


      forms.delete(
        form
      );

    }


    for (
      const control
      of Array.from(
        controls.keys()
      )
    ) {

      if (
        root.contains(
          control
        )
      ) {

        detachControl(
          control
        );

      }

    }

  };



  /* =======================================================
     ACCESSIBILITY
     ======================================================= */

  document.addEventListener(
    'focusin',
    event => {

      const target =

        event.target instanceof Element

          ? event.target

          : null;


      const element =

        target?.closest(
          '.vn-motion-reveal-pending'
        );


      if (
        element
      ) {

        finishReveal(
          element
        );

      }

    }
  );



  /* =======================================================
     SHOPIFY THEME EDITOR EVENTS
     ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    event => {

      initialize(
        event.target
      );

    }
  );


  document.addEventListener(
    'shopify:section:unload',
    event => {

      cleanup(
        event.target
      );

    }
  );



  /* =======================================================
     PAGE CACHE / BACK BUTTON SUPPORT
     ======================================================= */

  window.addEventListener(
    'pageshow',
    event => {

      for (
        const state
        of forms.values()
      ) {

        resetForm(
          state
        );

      }


      for (
        const state
        of controls.values()
      ) {

        resetMagnet(
          state
        );

      }


      if (
        event.persisted
      ) {

        for (
          const element
          of Array.from(
            pending
          )
        ) {

          finishReveal(
            element
          );

        }

      }

    }
  );



  /* =======================================================
     LAYOUT / VISIBILITY
     ======================================================= */

  const invalidateLayout = () => {

    layoutVersion +=
      1;

  };


  window.addEventListener(
    'resize',
    invalidateLayout,
    {
      passive: true
    }
  );


  window.addEventListener(
    'scroll',
    invalidateLayout,
    {
      passive: true
    }
  );


  document.addEventListener(
    'visibilitychange',
    () => {

      if (
        !document.hidden
      ) {

        return;

      }


      for (
        const state
        of controls.values()
      ) {

        resetMagnet(
          state
        );

      }

    }
  );



  /* =======================================================
     MOTION PREFERENCE / POINTER CHANGES
     ======================================================= */

  const refreshMotionCapabilities = () => {

    if (
      reduced.matches
    ) {

      for (
        const element
        of Array.from(
          pending
        )
      ) {

        finishReveal(
          element
        );

      }

    } else {

      registerReveals(
        document
      );

    }


    if (
      magnetsAllowed()
    ) {

      registerControls(
        document
      );

    } else {

      detachAllControls();

    }

  };


  reduced.addEventListener(
    'change',
    refreshMotionCapabilities
  );


  precise.addEventListener(
    'change',
    refreshMotionCapabilities
  );



  /* =======================================================
     START
     ======================================================= */

  if (
    document.readyState ===
      'loading'
  ) {

    document.addEventListener(

      'DOMContentLoaded',

      () =>
        initialize(
          document
        ),

      {
        once:
          true
      }

    );

  } else {

    initialize(
      document
    );

  }

})();

/* Ritual Studio enhancement. Native scrolling and navigation remain intact. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 801px)');
  let frame = 0;
  let observer;
  let progress;
  const tracked = new Set();
  const reveal = el => {
    el.classList.remove('ritual-story-pending');
    el.classList.add('ritual-story-visible');
    observer?.unobserve(el);
    tracked.delete(el);
  };
  const paint = () => {
    frame = 0;
    if (document.hidden || reduced.matches) return;
    const max = document.documentElement.scrollHeight - innerHeight;
    progress?.style.setProperty('--ritual-scroll',max > 0 ? Math.min(1,scrollY/max) : 0);
    if (desktop.matches) document.querySelectorAll('[data-ritual-hero]').forEach(hero => {
      const r = hero.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) return;
      hero.style.setProperty('--ritual-image-y',`${Math.max(-24,Math.min(25,-r.top*.075))}px`);
    });
  };
  const schedule = () => {if (!frame && !reduced.matches) frame = requestAnimationFrame(paint);};
  const initialize = (root=document) => {
    if (reduced.matches) return;
    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'ritual-scroll-progress';
      progress.setAttribute('aria-hidden','true');
      document.body.append(progress);
    }
    if ('IntersectionObserver' in window && !observer) observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {if (entry.isIntersecting) reveal(entry.target);});
    },{threshold:.08,rootMargin:'0px 0px 35px 0px'});
    root.querySelectorAll('.ritual-statement-grid > *, .ritual-scene-caption').forEach(el => {
      if (!observer || tracked.has(el) || el.classList.contains('ritual-story-visible') || el.getBoundingClientRect().top < innerHeight) return;
      el.classList.add('ritual-story-pending');tracked.add(el);observer.observe(el);
    });
    schedule();
  };
  addEventListener('scroll',schedule,{passive:true});
  addEventListener('resize',schedule,{passive:true});
  document.addEventListener('visibilitychange',()=>{if(document.hidden){cancelAnimationFrame(frame);frame=0;}else schedule();});
  addEventListener('pageshow',event=>{if(event.persisted) [...tracked].forEach(reveal);schedule();});
  document.addEventListener('focusin',event=>{const el=event.target.closest?.('.ritual-story-pending');if(el)reveal(el);});
  document.addEventListener('shopify:section:load',event=>initialize(event.target));
  document.addEventListener('shopify:section:unload',event=>{[...tracked].forEach(el=>{if(event.target.contains(el)){observer?.unobserve(el);tracked.delete(el);}});});
  reduced.addEventListener('change',()=>{if(reduced.matches){cancelAnimationFrame(frame);frame=0;[...tracked].forEach(reveal);document.querySelectorAll('[data-ritual-hero]').forEach(el=>el.style.removeProperty('--ritual-image-y'));}else initialize();});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initialize(),{once:true});else initialize();
})();

/* Keep the displayed price in step with the selected purchasable option. */
document.addEventListener("change", event => {
  const select = event.target instanceof Element ? event.target.closest('.vitality-product-form select[name="id"]') : null;
  if (!select) return;
  const option = select.selectedOptions[0];
  const scope = select.closest(".vitality-product-page");
  const price = scope?.querySelector(".vitality-product-price");
  if (price && option?.dataset.price) price.textContent = option.dataset.price;
  if (option?.value) { const url = new URL(location.href); url.searchParams.set("variant", option.value); history.replaceState(null, "", url); }
});
